import React from 'react';
import PropTypes from 'prop-types';

import { Wrapper, IconWrapper, LabelWrapper } from './index.styles';

import Icon from '../icon';

/* eslint-disable arrow-body-style */

/**
 * Renders a button. Supports icons.
 */
const Button = React.forwardRef(
  (
    {
      children,
      disabled,
      disabledAlt,
      hasLabel,
      size = 'regular',
      leftIconHeight = size === 'xsmall' ? 12 : 16,
      leftIconSrc,
      leftIconWidth = size === 'xsmall' ? 12 : 16,
      onClick,
      rightIconHeight = size === 'xsmall' ? 12 : 16,
      rightIconSrc,
      rightIconWidth = size === 'xsmall' ? 12 : 16,
      type = 'submit',
      variant = 'secondary',
      ...otherProps
    },
    ref,
  ) => {
    return (
      <Wrapper
        {...otherProps}
        disabled={disabled}
        disabledAlt={disabledAlt}
        hasLabel={hasLabel ?? Boolean(children)}
        onClick={onClick}
        ref={ref}
        size={size}
        type={type}
        variant={variant}
      >
        {leftIconSrc && (
          <IconWrapper>
            <Icon alt="" src={leftIconSrc} width={leftIconWidth} height={leftIconHeight} inline />
          </IconWrapper>
        )}
        {children && <LabelWrapper size={size}>{children}</LabelWrapper>}
        {rightIconSrc && (
          <IconWrapper>
            <Icon
              alt=""
              src={rightIconSrc}
              width={rightIconWidth}
              height={rightIconHeight}
              inline
            />
          </IconWrapper>
        )}
      </Wrapper>
    );
  },
);

Button.displayName = 'Button';

Button.propTypes = {
  /**
   * Component that should render the button.
   */
  as: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  /**
   * Button label.
   */
  children: PropTypes.node,
  /**
   * Whether to disable the button.
   */
  disabled: PropTypes.bool,
  /**
   * Whether to use alternative styling for disabled button.
   */
  disabledAlt: PropTypes.bool,
  /**
   * Whether to render button with 100% width.
   */
  fullWidth: PropTypes.bool,
  /**
   * Whether the button has a (text-based) label. Usually determined by the presence of `children`.
   */
  hasLabel: PropTypes.bool,
  /**
   * Left icon height in pixels (recommended if icon URL is used).
   */
  leftIconHeight: PropTypes.number,
  /**
   * Left icon, left icon source or left icon URL.
   */
  leftIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Left icon width in pixels (recommended if icon URL is used).
   */
  leftIconWidth: PropTypes.number,
  /**
   * Function to run on click.
   */
  onClick: PropTypes.func,
  /**
   * Right icon height in pixels (recommended if icon URL is used).
   */
  rightIconHeight: PropTypes.number,
  /**
   * Right icon, right icon source or right icon URL.
   */
  rightIconSrc: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.node,
    PropTypes.object,
    PropTypes.string,
  ]),
  /**
   * Right icon width in pixels (recommended if icon URL is used).
   */
  rightIconWidth: PropTypes.number,
  /**
   * Button size.
   */
  size: PropTypes.oneOf(['large', 'regular', 'small', 'xsmall']),
  /**
   * Button title.
   */
  title: PropTypes.string,
  /**
   * Button type.
   */
  type: PropTypes.oneOf(['button', 'reset', 'submit']),
  /**
   * Button variant. "-round" variants are typically used for icon-only buttons.
   */
  variant: PropTypes.oneOf([
    'primary',
    'primary-round',
    'destructive',
    'destructive-round',
    'secondary',
    'secondary-round',
    'tertiary',
    'tertiary-round',
  ]),
};

export default Button;
